import React from 'react';
import { useTranslation } from 'react-i18next';

import { Arrow } from 'icons/untitledui';
import * as DropdownStyled from 'theme/components/Dropdown.style';
import * as Styled from '../../styles/FilterGroupingControl.style';
import { FilterKey, FilterOptions, FilterValues } from './_types';

interface FilterMenuProps {
  values: FilterValues;
  options: FilterOptions;
  onSelectCategory: (category: FilterKey, anchorTop: number) => void;
  onReset: () => void;
  onPrimaryClick?: () => void;
}

const ORDER: FilterKey[] = [
  'urgency',
  'department',
  'people',
  'tags',
  'visibility',
  'source',
  'signal',
];

function hasOptions(key: FilterKey, options: FilterOptions): boolean {
  switch (key) {
    case 'department':
      return options.departments.length > 0;
    case 'people':
      return options.people.length > 0;
    case 'tags':
      return options.tags.length > 0;
    case 'source':
    case 'signal':
      return options.signals.length > 0;
    case 'urgency':
    case 'visibility':
      return true;
    default:
      return true;
  }
}

const useSummaries = (values: FilterValues, options: FilterOptions) => {
  const { t } = useTranslation('pageEvents');

  const summarize = (key: FilterKey): string => {
    switch (key) {
      case 'urgency':
        return values.urgency
          ? t(`filterGrouping.urgency.options.${values.urgency}`)
          : t('filterGrouping.summary.all');
      case 'department':
        return (
          options.departments.find((d) => d.id === values.department)?.name ??
          t('filterGrouping.summary.all')
        );
      case 'people':
        return (
          options.people.find((p) => p.id === values.person)?.name ??
          t('filterGrouping.summary.all')
        );
      case 'tags':
        return (
          options.tags.find((tag) => tag.id === values.tag)?.name ??
          t('filterGrouping.summary.all')
        );
      case 'visibility':
        return values.visibility
          ? t(`filterGrouping.visibility.options.${values.visibility}`)
          : t('filterGrouping.summary.all');
      case 'source':
        return values.source
          ? t(`filterGrouping.source.options.${values.source}`)
          : t('filterGrouping.summary.all');
      case 'signal':
        return (
          options.signals.find((signal) => signal.id === values.signal)?.name ??
          t('filterGrouping.summary.all')
        );
      default:
        return '';
    }
  };

  return { summarize };
};

export default function FilterMenu({
  values,
  options,
  onSelectCategory,
  onReset,
  onPrimaryClick,
}: FilterMenuProps): JSX.Element {
  const { t } = useTranslation('pageEvents');
  const { summarize } = useSummaries(values, options);
  const hasActiveFilters = Object.values(values).some((v) => v !== null);

  return (
    <Styled.PopoverCard onClick={() => onPrimaryClick?.()}>
      <Styled.List>
        <Styled.MenuHeader>
          <DropdownStyled.SectionHeader>
            {t('filterGrouping.filter.title')}
          </DropdownStyled.SectionHeader>
          {hasActiveFilters && (
            <Styled.HeaderResetButton
              type="button"
              $size="tiny"
              onClick={onReset}
            >
              <Arrow.RefreshCcw01 />
              {t('filterGrouping.filter.reset')}
            </Styled.HeaderResetButton>
          )}
        </Styled.MenuHeader>
        {ORDER.filter((key) => hasOptions(key, options)).map((key) => (
          <DropdownStyled.ItemButton
            key={key}
            type="button"
            role="menuitem"
            $hasSubContent
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              onSelectCategory(
                key,
                e.currentTarget.offsetTop + e.currentTarget.offsetHeight + 10
              );
            }}
          >
            <DropdownStyled.ItemContent>
              <span>{t(`filterGrouping.filter.categories.${key}`)}</span>
              <DropdownStyled.ItemSubContent>
                {summarize(key)}
              </DropdownStyled.ItemSubContent>
            </DropdownStyled.ItemContent>
            <DropdownStyled.ItemIcon>
              <Arrow.ChevronRight />
            </DropdownStyled.ItemIcon>
          </DropdownStyled.ItemButton>
        ))}
      </Styled.List>
    </Styled.PopoverCard>
  );
}
